/**
 * Thin REST wrapper over `POST /api/v1/authenticate/widget` (§2.1).
 *
 * The widget never talks to any other REST endpoint — everything else flows
 * over Socket.IO after this call issues a token.
 */

import type { AuthenticateRequest, AuthenticateResponse } from './types';

export interface AuthenticateOptions {
  baseUrl: string;
  body: AuthenticateRequest;
  /** Override fetch for tests. */
  fetchImpl?: typeof fetch;
}

export async function authenticateWidget(
  opts: AuthenticateOptions
): Promise<AuthenticateResponse> {
  const f = opts.fetchImpl ?? fetch;
  const res = await f(`${opts.baseUrl}/api/v1/authenticate/widget`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(opts.body),
    credentials: 'omit',
  });

  if (!res.ok) {
    let payload: any = null;
    try {
      payload = await res.json();
    } catch {
      // ignore
    }
    const code = payload?.code || payload?.error || `http_${res.status}`;
    const err = new Error(
      `authenticate_widget_failed: ${code}`
    ) as Error & { status: number; code: string; payload: unknown };
    err.status = res.status;
    err.code = code;
    err.payload = payload;
    throw err;
  }

  return (await res.json()) as AuthenticateResponse;
}

export interface FetchWidgetConfigOptions {
  baseUrl: string;
  publicKey: string;
  fetchImpl?: typeof fetch;
}

export interface WidgetConfigResponse {
  widget: {
    name: string;
    title: string;
    intro_text: string;
    greeting_message: string;
    welcome_popup_timeout: number;
    welcome_popup_message: string;
    allow_upload: boolean;
    max_upload_mb: number;
  };
}

/**
 * Lightweight mount-time probe. Hits the read-only widget config endpoint
 * to determine whether the embed is enabled and the current origin is on
 * the provider's allowlist. No visitor is created. On 401/403 the widget
 * unmounts silently (fatalConfigError), so a disabled widget never shows
 * even a briefly-visible bubble.
 */
export async function fetchWidgetConfig(
  opts: FetchWidgetConfigOptions
): Promise<WidgetConfigResponse> {
  const f = opts.fetchImpl ?? fetch;
  const qs = new URLSearchParams({ public_key: opts.publicKey });
  const res = await f(`${opts.baseUrl}/api/v1/widget/config?${qs.toString()}`, {
    method: 'GET',
    credentials: 'omit',
  });

  if (!res.ok) {
    let payload: any = null;
    try { payload = await res.json(); } catch { /* ignore */ }
    const code = payload?.code || payload?.error || `http_${res.status}`;
    const err = new Error(
      `widget_config_failed: ${code}`
    ) as Error & { status: number; code: string; payload: unknown };
    err.status = res.status;
    err.code = code;
    err.payload = payload;
    throw err;
  }

  return (await res.json()) as WidgetConfigResponse;
}
